/**
 * Putting a grace note into a voice, which is insertion rather than overwriting.
 *
 * Every other kind of note entry writes over what stands at the caret, because a bar has to
 * stay as long as it was -- `entry-voice` says why. A grace note is the one note that is not
 * subject to that arithmetic at all: it occupies no ticks, takes its sounding time from the
 * note it leans on, and so can be slipped in front of that note without anything else in the
 * bar moving. It is therefore written here and not there.
 *
 * A grace note leans forward. It is placed immediately before the note that starts where the
 * caret stands, after any graces already leaning on that note, so that a run typed left to
 * right comes out in the order it was typed. A caret standing after the last note of a bar
 * gives a grace that leans on the first note of the next bar, which is ordinary notation and
 * is what the schedule already expects.
 *
 * Nothing here knows about the score, the caret or commands: a voice, a moment and an event
 * go in, and the events the voice should hold come out.
 */

import { occupiedTicks } from "../document/query";
import type { MusicEvent, Voice } from "../document/types";
import { silence } from "./entry-voice";
import type { Written } from "./entry-voice";

/** Ticks of the bar a voice accounts for. */
function filled(voice: Voice, capacity: number): number {
  return voice.events.reduce((total, event) => total + occupiedTicks(event, capacity), 0);
}

/**
 * Where a grace written at `onset` belongs in the list.
 *
 * The note it leans on, when one starts there; otherwise the end of the run, which is the
 * grace that leans across the bar line. A caret in the middle of a sounding note has no note
 * to lean on and gets `-1`.
 */
function leansOn(voice: Voice, onset: number, capacity: number): number {
  let clock = 0;
  for (let at = 0; at < voice.events.length; at += 1) {
    const event = voice.events[at];
    if (event === undefined) continue;
    const length = occupiedTicks(event, capacity);
    // A grace already standing at this moment is stepped over, so the new one joins the run
    // behind it rather than in front of it.
    if (clock === onset && length > 0) return at;
    if (clock > onset) return -1;
    clock += length;
  }
  return onset >= clock ? voice.events.length : -1;
}

/**
 * Slip a grace note into `voice` at `onset`, leaving the bar exactly as long as it was.
 *
 * A caret past the end of what the voice accounts for is filled in with rests first, the
 * same as writing an ordinary note there would do.
 */
export function insertGrace(
  voice: Voice,
  onset: number,
  event: MusicEvent,
  capacity: number,
): Written | string {
  if (onset < 0 || !Number.isInteger(onset)) return "a grace note can only be written on a tick";
  if (onset > capacity) return "that is past the end of the bar";

  const accounted = filled(voice, capacity);
  if (onset > accounted) {
    const gap = silence(onset - accounted);
    if (gap === null) return "what is left of the bar cannot be written as plain rests";
    return {
      events: [...voice.events, ...gap, event],
      index: voice.events.length + gap.length,
    };
  }

  const index = leansOn(voice, onset, capacity);
  if (index < 0) return "a grace note leans on the note after it, and nothing starts there";
  return {
    events: [...voice.events.slice(0, index), event, ...voice.events.slice(index)],
    index,
  };
}

/**
 * The last grace note leaning on the moment at `onset`, or `-1` when none does.
 *
 * Last rather than first because this answers the question "undo the grace I just typed",
 * and a run of graces is typed in the order it is read.
 */
export function lastGraceAt(voice: Voice, onset: number, capacity: number): number {
  const index = leansOn(voice, onset, capacity);
  if (index < 0) return -1;
  for (let at = index - 1; at >= 0; at -= 1) {
    const event = voice.events[at];
    if (event === undefined) return -1;
    if (occupiedTicks(event, capacity) > 0) return -1;
    if (event.kind === "note" && event.grace !== null) return at;
  }
  return -1;
}
